/**
 * Dispatch board (/dispatch, tracker item 40).
 *
 * Day view: technician columns on a scrollable time axis with a shared
 * expandable no-time row and an unassigned queue. Week view summarizes each
 * technician's day. Backlog opens on demand. Drag-and-drop and the Schedule
 * dialog share the check, confirmation, PATCH, and stale-job refresh path.
 * Phones group the work by day and then technician. "Optimize route" (day
 * view column header, phone list) proposes a shorter order for one
 * technician's day (tracker item 41, phase 3). Phase 4: "~12 min" travel
 * chips between consecutive cards (day grid and phone list), each day's
 * driving total in the week view, a "location unknown" marker on cards
 * whose property has no map location, and "Suggest for the visible
 * backlog" (BulkSuggestDialog).
 *
 * URL-backed `date` (omitted = today in the company timezone, from the
 * server), `view` and `group` (technician columns, or `route`: rows by
 * the stored Job route with informational technician coverage; tracker item
 * 57). Route-gated to ADMIN / OFFICE_MANAGER like the backend.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { JobEnrollmentChip } from '../features/service-bundles/EnrollmentChip';
import { enrollmentRefOf } from '../features/service-bundles/bundleUi';
import { createAPIClient, IDispatchApi } from '../api';
import * as types from '../api/types';
import { inputClass, primaryButtonClass, secondaryButtonClass } from '../components/formStyles';
import { ScheduleDialog, ScheduleDialogJob, scheduleDialogJob } from '../components/ScheduleDialog';
import { getErrorMessage } from '../utils/apiError';
import { formatJobDate } from '../utils/jobPresentation';
import { jobStatusLabel } from './jobUi';
import { routeName } from './routeUi';
import {
  BoardGroup,
  DayAxis,
  UNROUTED_GROUP,
  JOB_CHANGED_MESSAGE,
  SUGGESTION_STALE_MESSAGE,
  PIXELS_PER_MINUTE,
  SaveOutcome,
  axisHeight,
  axisHours,
  boardSearch,
  cardGeometry,
  conflictLabel,
  coverageLabel,
  dayAxis,
  dispatchPrintPath,
  dropTime,
  formatArrivalWindow,
  formatDayHeading,
  formatDuration,
  formatTime,
  formatTimeRange,
  jobsFor,
  layoutLanes,
  routeCoverage,
  routeGroups,
  routeJobs,
  saveScheduleChange,
  shadeBlocks,
  shiftDate,
  statusClasses,
  timeOffOn,
  timeOrdered,
  unscheduleChange,
  visibleTechnicians,
  weekDates,
} from './dispatchUi';
import { Toast } from '../components/Toast';
import { ScheduleConfirmation, useScheduleConfirmation } from '../components/ScheduleConfirmation';
import { SuggestPanel } from '../components/SuggestPanel';
import { candidateTimeText, suggestionOrigin } from './suggestionUi';
import { OptimizeDayDialog } from '../components/OptimizeDayDialog';
import { OPTIMIZE_CHANGED_MESSAGE } from './optimizeUi';
import { BulkSuggestDialog } from '../components/BulkSuggestDialog';
import {
  TRAVEL_CHIP_HEIGHT,
  bulkAppliedMessage,
  bulkCandidates,
  dayTravelSummary,
  locationUnknown,
  travelChipLabel,
  travelChipText,
  travelChipTop,
  travelLegsByJob,
} from './travelUi';

/** A suggestion chosen in the Suggest panel, applied through the Schedule dialog (tracker item 41). */
interface ChosenSuggestion {
  prefill: { scheduledDate: string; scheduledStartTime: string; assignedTo: string };
  suggestion: { origin: types.ScheduleOrigin; label: string };
}

const BACKLOG_PAGE = 25;
const COLUMN_WIDTH = 'min-w-[15rem] flex-1';
const UNASSIGNED = '__unassigned__';

type DropTarget =
  | { kind: 'grid'; assignedTo: string | null; date: string; offsetY: number }
  | { kind: 'noTime'; assignedTo: string | null; date: string }
  | { kind: 'cell'; assignedTo: string | null; date: string }
  | { kind: 'backlog' };

/** The change a drop makes: grid = date + assignee + snapped time; no-time strip clears the time; week cell keeps it. */
export function dropChange(job: types.BoardJob, target: DropTarget, axis: DayAxis): types.ScheduleChange | null {
  switch (target.kind) {
    case 'grid':
      return { scheduledDate: target.date, assignedTo: target.assignedTo, scheduledStartTime: dropTime(target.offsetY, axis) };
    case 'noTime':
      return { scheduledDate: target.date, assignedTo: target.assignedTo, scheduledStartTime: null };
    case 'cell':
      return { scheduledDate: target.date, assignedTo: target.assignedTo };
    case 'backlog':
      return unscheduleChange(job);
    default:
      return null;
  }
}

function ConflictBadge({ conflicts }: { conflicts: types.ScheduleConflict[] }) {
  if (conflicts.length === 0) return null;
  const labels = [...new Set(conflicts.map((c) => conflictLabel(c.type)))];
  return (
    <span aria-label={`Warnings: ${labels.join(', ')}`}
      className="ml-1 inline-flex items-center rounded bg-red-100 px-1.5 py-0.5 text-[11px] font-semibold text-red-800">
      {conflicts.length} warning{conflicts.length === 1 ? '' : 's'}: {labels.join(', ')}
    </span>
  );
}

/** "Location unknown" marker (tracker item 41, phase 4): the property has no map location, so drive times to it are estimates. */
function LocationUnknownMarker({ compact }: { compact?: boolean }) {
  const label = 'Location unknown: no map location for this property, drive times are estimated';
  return (
    <span role="img" aria-label={label} title={label} data-location-unknown
      className={`inline-flex shrink-0 items-center gap-0.5 rounded bg-amber-100 font-semibold text-amber-900 ${compact ? 'mr-1 px-0.5 text-[10px] leading-4' : 'px-1 text-[11px]'}`}>
      <svg aria-hidden="true" viewBox="0 0 12 12" className="h-3 w-3"><path d="M6 1a3.5 3.5 0 0 0-3.5 3.5C2.5 7.2 6 11 6 11s3.5-3.8 3.5-6.5A3.5 3.5 0 0 0 6 1Z" fill="none" stroke="currentColor" strokeWidth="1.3" /><path d="M2 2l8 8" stroke="currentColor" strokeWidth="1.3" /></svg>
      {!compact && 'Location unknown'}
    </span>
  );
}

/** "~12 min" drive chip (tracker item 41, phase 4). */
function TravelChip({ leg, unit, style, className = '' }: { leg: types.BoardTravelLeg; unit: unknown; style?: React.CSSProperties; className?: string }) {
  const label = travelChipLabel(leg, unit);
  return (
    <span role="note" aria-label={label} title={label} style={style} data-travel-to={leg.toJobId}
      className={`inline-flex items-center whitespace-nowrap rounded-full border px-1.5 text-[10px] font-semibold leading-4 shadow-sm ${leg.unknown ? 'border-amber-300 bg-amber-50 text-amber-900' : 'border-gray-300 bg-white text-gray-700'} ${className}`}>
      {travelChipText(leg)}
    </span>
  );
}

interface CardProps {
  job: types.BoardJob;
  compact?: boolean;
  /** Route view: show the technician, and no drag (the route board has no drop targets) */
  routeView?: boolean;
  style?: React.CSSProperties;
  onOpen: (job: types.BoardJob) => void;
  onSchedule: (job: types.BoardJob) => void;
  onDragStart: (job: types.BoardJob, event: React.DragEvent) => void;
}

function JobCard({ job, compact, routeView, style, onOpen, onSchedule, onDragStart }: CardProps) {
  const assignee = job.assignedToEmail ?? 'Unassigned';
  return (
    <div
      role="button"
      tabIndex={0}
      draggable={job.schedulable && !routeView}
      onDragStart={(event) => onDragStart(job, event)}
      onClick={() => onOpen(job)}
      onKeyDown={(event) => { if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) { event.preventDefault(); onOpen(job); } }}
      data-job-id={job.id}
      aria-label={`${job.customer.name}, ${formatTimeRange(job.scheduledStartTime, job.durationMinutes)}, ${routeView ? `${assignee}, ` : ''}${jobStatusLabel(job.status as types.JobStatus)}${job.conflicts.length ? `, warnings: ${job.conflicts.map((c) => conflictLabel(c.type)).join(', ')}` : ''}${locationUnknown(job) ? ', location unknown' : ''}. Open details`}
      style={style}
      className={`overflow-hidden rounded border-l-4 shadow-sm ${compact ? 'px-1 py-0 text-xs leading-5' : 'px-2 py-1.5 text-sm'} ${statusClasses(job.status)} ${job.schedulable && !routeView ? 'cursor-grab' : 'cursor-pointer'} focus:outline-none focus:ring-2 focus:ring-blue-500`}
    >
      {compact ? <div className="truncate font-semibold">{locationUnknown(job) && <LocationUnknownMarker compact />}{job.conflicts.length ? `${job.conflicts.length} warning${job.conflicts.length === 1 ? '' : 's'} · ` : ''}Details / Schedule · {formatTime(job.scheduledStartTime)} · {job.customer.name}{routeView ? ` · ${assignee}` : ''} · {jobStatusLabel(job.status as types.JobStatus)}{enrollmentRefOf(job) ? ` · ${job.bundleName} ${job.enrollmentStep} of ${job.enrollmentStepCount}` : ''}</div> : <div className="flex items-start justify-between gap-1">
        <span className="font-semibold text-gray-900">
          {formatTimeRange(job.scheduledStartTime, job.durationMinutes)}
        </span>
        <ConflictBadge conflicts={job.conflicts} />
      </div>}
      {!compact && <div className="truncate font-medium text-gray-900">{job.customer.name}</div>}
      {!compact && enrollmentRefOf(job) && <div className="mt-0.5"><JobEnrollmentChip job={job} /></div>}
      {!compact && <div className="flex min-w-0 items-center gap-1 text-gray-600"><span className="truncate">{job.property.address}</span>{locationUnknown(job) && <LocationUnknownMarker />}</div>}
      {!compact && routeView && <div className="truncate text-xs text-gray-700">{assignee}</div>}
      {!compact && <div className="text-xs text-gray-600">{jobStatusLabel(job.status as types.JobStatus)}</div>}
      {!compact && job.arrivalWindowMinutes && job.scheduledStartTime && (
        <div className="truncate text-gray-500">Arrive {formatArrivalWindow(job.scheduledStartTime, job.arrivalWindowMinutes)}</div>
      )}
      {job.schedulable && !compact && (
        <button type="button" className="mt-0.5 text-[11px] font-semibold text-blue-700 hover:underline"
          onClick={(event) => { event.stopPropagation(); onSchedule(job); }}>
          Schedule…
        </button>
      )}
    </div>
  );
}

export function DispatchPage() {
  const api = useMemo(() => createAPIClient() as IDispatchApi, []);
  const [params, setParams] = useSearchParams();
  const requestedDate = params.get('date') || null;
  const view: types.BoardView = params.get('view') === 'week' ? 'week' : 'day';
  const group: BoardGroup = params.get('group') === 'route' ? 'route' : 'technician';

  const [board, setBoard] = useState<types.DispatchBoard | null>(null);
  const [backlog, setBacklog] = useState<types.BoardJob[]>([]);
  const [backlogCursor, setBacklogCursor] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [selectedTechs, setSelectedTechs] = useState<string[]>([]);
  /** Filter "Bundle visits only" (tracker item 85): only jobs that belong to a bundle enrollment */
  const [bundleOnly, setBundleOnly] = useState(false);
  const [backlogOpen, setBacklogOpen] = useState(false);
  const [phoneWidth, setPhoneWidth] = useState(() => window.innerWidth < 768);
  const [dialogJob, setDialogJob] = useState<ScheduleDialogJob | null>(null);
  /** The board job behind the open dialog (for "Suggest…") and the suggestion it applies, if any */
  const [dialogSource, setDialogSource] = useState<types.BoardJob | null>(null);
  const [chosen, setChosen] = useState<ChosenSuggestion | null>(null);
  const [suggestFor, setSuggestFor] = useState<{ job: types.BoardJob; notice: string | null } | null>(null);
  const [drawerJob, setDrawerJob] = useState<types.BoardJob | null>(null);
  const [optimizeFor, setOptimizeFor] = useState<{ userId: string; label: string; date: string } | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const dragged = useRef<types.BoardJob | null>(null);
  const boardScroll = useRef<HTMLDivElement | null>(null);
  const backlogToggle = useRef<HTMLButtonElement>(null);
  const backlogSearch = useRef<HTMLInputElement>(null);
  const backlogPanel = useRef<HTMLElement>(null);
  const scrollPosition = useRef<{ top: number; left: number } | null>(null);
  const confirmation = useScheduleConfirmation();
  const phoneBacklogModal = backlogOpen && phoneWidth;

  useEffect(() => {
    const onResize = () => setPhoneWidth(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (backlogOpen) backlogSearch.current?.focus();
  }, [backlogOpen]);
  useEffect(() => {
    if (!backlogOpen || dialogJob || drawerJob || suggestFor || optimizeFor || bulkOpen || confirmation.message) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { setBacklogOpen(false); backlogToggle.current?.focus(); }
      if (event.key !== 'Tab' || !phoneWidth) return;
      const focusable = backlogPanel.current?.querySelectorAll<HTMLElement>('button:not([disabled]), input:not([disabled]), a[href]');
      if (!focusable?.length) return;
      const first = focusable[0], last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [backlogOpen, dialogJob, drawerJob, suggestFor, optimizeFor, bulkOpen, confirmation.message, phoneWidth]);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [nextBoard, nextBacklog] = await Promise.all([
        api.getDispatchBoard(requestedDate ?? undefined, view),
        api.getDispatchBacklog({ search: appliedSearch, limit: BACKLOG_PAGE }),
      ]);
      setBoard(nextBoard);
      setBacklog(nextBacklog.data);
      setBacklogCursor(nextBacklog.nextCursor);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load the dispatch board'));
    } finally {
      setLoading(false);
    }
  }, [api, requestedDate, view, appliedSearch]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  const date = board?.date ?? requestedDate ?? '';
  const technicians = useMemo(
    () => (board ? visibleTechnicians(board.technicians, board.jobs) : []),
    [board]
  );
  const allTechnicians = board?.technicians ?? [];
  const gridBoard = useMemo(() => (board && bundleOnly ? { ...board, jobs: board.jobs.filter((job) => Boolean(enrollmentRefOf(job))) } : board), [board, bundleOnly]);
  const shownBacklog = useMemo(() => (bundleOnly ? backlog.filter((job) => Boolean(enrollmentRefOf(job))) : backlog), [backlog, bundleOnly]);
  const shownTechnicians = selectedTechs.length ? technicians.filter((t) => selectedTechs.includes(t.id)) : technicians;
  const axis = useMemo(
    () => dayAxis([...(board ? [board.companyWorkHours] : []), ...technicians.map((t) => t.workHours)], board?.jobs ?? []),
    [board, technicians]
  );

  const go = (nextDate: string | null, nextView: types.BoardView = view, nextGroup: BoardGroup = group) => {
    setParams(new URLSearchParams(boardSearch(nextDate, nextView, nextGroup).slice(1)));
    setDrawerJob(null);
  };

  useEffect(() => {
    if (!board || view !== 'day' || !boardScroll.current) return;
    if (scrollPosition.current) {
      boardScroll.current.scrollTop = scrollPosition.current.top;
      boardScroll.current.scrollLeft = scrollPosition.current.left;
      scrollPosition.current = null;
      return;
    }
    const start = board.date === board.today ? Math.max(0, board.nowMinutes - 60) : Number(board.companyWorkHours.startTime.slice(0, 2)) * 60;
    boardScroll.current.scrollTop = Math.max(0, (start - axis.startMinutes) * PIXELS_PER_MINUTE);
  }, [board, view, axis.startMinutes]);

  const closeDialog = () => { setDialogJob(null); setDialogSource(null); setChosen(null); };

  const afterOutcome = async (outcome: Exclude<SaveOutcome, { status: 'cancelled' }>) => {
    if (boardScroll.current) scrollPosition.current = { top: boardScroll.current.scrollTop, left: boardScroll.current.scrollLeft };
    const source = dialogSource;
    closeDialog();
    setDrawerJob(null);
    if (outcome.status === 'stale') {
      // Nothing was written: refresh, then show fresh suggestions for the same job.
      setNotice(null);
      await load();
      if (source) setSuggestFor({ job: source, notice: SUGGESTION_STALE_MESSAGE });
      return;
    }
    if (outcome.status === 'changed') setNotice(JOB_CHANGED_MESSAGE);
    else {
      const warnings = outcome.result.conflicts.length;
      setNotice(warnings ? `Schedule saved with ${warnings} warning${warnings === 1 ? '' : 's'}.` : 'Schedule saved.');
    }
    await load();
  };

  const openDialog = (job: types.BoardJob, pick: ChosenSuggestion | null = null) => {
    setNotice(null);
    setDrawerJob(null);
    setDialogSource(job);
    setChosen(pick);
    setDialogJob(scheduleDialogJob(job));
  };

  const openSuggest = (job: types.BoardJob) => {
    setNotice(null);
    setDrawerJob(null);
    closeDialog();
    setSuggestFor({ job, notice: null });
  };

  const applySuggestion = (job: types.BoardJob, candidate: types.SuggestionCandidate) => {
    setSuggestFor(null);
    openDialog(job, {
      prefill: { scheduledDate: candidate.date, scheduledStartTime: candidate.startTime, assignedTo: candidate.technicianId },
      suggestion: {
        origin: suggestionOrigin(candidate),
        label: `${candidate.technicianEmail}, ${formatDayHeading(candidate.date)} ${candidateTimeText(candidate)}`,
      },
    });
  };

  const openOptimize = (userId: string, label: string, day: string) => {
    setNotice(null);
    setDrawerJob(null);
    closeDialog();
    setOptimizeFor({ userId, label, date: day });
  };

  const bulkJobs = bulkCandidates(backlog);
  const openBulk = () => {
    setNotice(null);
    setDrawerJob(null);
    closeDialog();
    setBulkOpen(true);
  };

  const afterOptimize = async (message: string) => {
    if (boardScroll.current) scrollPosition.current = { top: boardScroll.current.scrollTop, left: boardScroll.current.scrollLeft };
    setOptimizeFor(null);
    setNotice(message);
    await load();
  };

  const onDragStart = (job: types.BoardJob, event: React.DragEvent) => {
    if (!job.schedulable) { event.preventDefault(); return; }
    dragged.current = job;
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', job.id);
  };

  const allowDrop = (event: React.DragEvent) => { if (dragged.current) { event.preventDefault(); event.dataTransfer.dropEffect = 'move'; } };

  const handleDrop = async (target: DropTarget) => {
    const job = dragged.current;
    dragged.current = null;
    if (!job || busy) return;
    const change = dropChange(job, target, axis);
    if (!change) return;
    setBusy(true);
    setNotice(null);
    try {
      const outcome = await saveScheduleChange(api, job, change, confirmation.confirm);
      if (outcome.status !== 'cancelled') await afterOutcome(outcome);
    } catch (err) {
      setError(getErrorMessage(err, 'Could not save the schedule'));
    } finally {
      setBusy(false);
    }
  };

  const loadMoreBacklog = async () => {
    if (!backlogCursor) return;
    try {
      const page = await api.getDispatchBacklog({ search: appliedSearch, limit: BACKLOG_PAGE, after: backlogCursor });
      setBacklog((rows) => [...rows, ...page.data]);
      setBacklogCursor(page.nextCursor);
    } catch (err) {
      setError(getErrorMessage(err, 'Could not load more jobs'));
    }
  };

  const cardHandlers = { onOpen: setDrawerJob, onSchedule: openDialog, onDragStart };
  const columns: Array<{ id: string | null; label: string; hours: types.WorkHours | null }> = [
    { id: null, label: 'Unassigned', hours: null },
    ...shownTechnicians.map((t) => ({ id: t.id, label: t.email, hours: t.workHours })),
  ];

  return (
    <main className="mx-auto max-w-[100rem] px-4 py-6 sm:px-6 lg:px-8">
      <div aria-hidden={phoneBacklogModal} className="mb-4 rounded-xl bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div><h1 className="text-2xl font-bold text-gray-900">Dispatch</h1>
            {board && <p className="text-sm text-gray-600">{view === 'week' ? `Week of ${formatJobDate(board.from)} to ${formatJobDate(board.to)}` : formatDayHeading(board.date)}{board.date === board.today && view === 'day' && ' (today)'} · Times in {board.timezone}</p>}
          </div>
          <div className="flex flex-wrap items-center gap-2" role="toolbar" aria-label="Board navigation">
          <button type="button" className={secondaryButtonClass} disabled={!date} onClick={() => go(shiftDate(date, view, -1))}>
            <span aria-hidden="true">←</span><span className="sr-only">{view === 'week' ? 'Previous week' : 'Previous day'}</span>
          </button>
          <button type="button" className={secondaryButtonClass} onClick={() => go(null)}>Today</button>
          <button type="button" className={secondaryButtonClass} disabled={!date} onClick={() => go(shiftDate(date, view, 1))}>
            <span aria-hidden="true">→</span><span className="sr-only">{view === 'week' ? 'Next week' : 'Next day'}</span>
          </button>
          <label className="sr-only" htmlFor="dispatch-date">Date</label>
          <input id="dispatch-date" type="date" className={`${inputClass} w-auto`} value={date} onChange={(event) => event.target.value && go(event.target.value)} />
          <div className="inline-flex rounded-md border border-gray-300" role="group" aria-label="View">
            {(['day', 'week'] as const).map((v) => (
              <button key={v} type="button" aria-pressed={view === v}
                className={`px-3 py-2 text-sm font-semibold ${view === v ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'} ${v === 'day' ? 'rounded-l-md' : 'rounded-r-md'}`}
                onClick={() => go(requestedDate, v)}>
                {v === 'day' ? 'Day' : 'Week'}
              </button>
            ))}
          </div>
          <div className="inline-flex rounded-md border border-gray-300" role="group" aria-label="Group by">
            {(['technician', 'route'] as const).map((g) => (
              <button key={g} type="button" aria-pressed={group === g}
                className={`px-3 py-2 text-sm font-semibold ${group === g ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'} ${g === 'technician' ? 'rounded-l-md' : 'rounded-r-md'}`}
                onClick={() => go(requestedDate, view, g)}>
                {g === 'technician' ? 'Technicians' : 'Routes'}
              </button>
            ))}
          </div>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-3 border-t border-gray-100 pt-3">
          <button ref={backlogToggle} type="button" className={secondaryButtonClass} aria-expanded={backlogOpen} aria-controls="dispatch-backlog" onClick={() => setBacklogOpen((open) => !open)}>
            Backlog · {backlog.length} loaded{backlogCursor ? '+' : ''}
          </button>
          {group === 'technician' && <fieldset className="flex min-w-0 flex-wrap items-center gap-2 text-sm"><legend className="sr-only">Technicians</legend>
            <span className="font-medium text-gray-700">Technicians</span>
            <button type="button" className={`rounded-full px-3 py-1 ${selectedTechs.length === 0 ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`} aria-pressed={selectedTechs.length === 0} onClick={() => setSelectedTechs([])}>All</button>
            {technicians.map((t) => <label key={t.id} className="inline-flex cursor-pointer items-center gap-1 rounded-full border border-gray-200 px-2 py-1 text-gray-700"><input type="checkbox" checked={selectedTechs.includes(t.id)} onChange={() => setSelectedTechs((ids) => ids.includes(t.id) ? ids.filter((id) => id !== t.id) : [...ids, t.id])} />{t.email}</label>)}
          </fieldset>}
          <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-gray-700"><input type="checkbox" className="h-4 w-4" checked={bundleOnly} onChange={(event) => setBundleOnly(event.target.checked)} />Bundle visits only</label>
          {date && view === 'day' && (
            <Link to={dispatchPrintPath(date)} className={`${secondaryButtonClass} inline-flex items-center`}>Print day sheets</Link>
          )}
        </div>
      </div>
      {notice && <Toast kind="info">{notice}</Toast>}
      {error && <Toast kind="error">{error}</Toast>}
      {board?.truncated && (
        <p role="alert" className="mb-3 rounded bg-amber-50 p-3 text-sm text-amber-900">
          Showing the first {board.cap} jobs only. Narrow the range (day view) to see the rest.
        </p>
      )}
      {loading && !board && <p role="status" className="text-gray-600">Loading board…</p>}

      {board && (
        <div className="flex flex-col gap-4 xl:flex-row">
          <div aria-hidden={phoneBacklogModal} className="min-w-0 flex-1">
            {group === 'route' ? (
              <RouteBoard board={gridBoard ?? board} cardHandlers={cardHandlers} onDay={day => go(day, 'day')} />
            ) : <>
            {/* Tablet and up: the grid (drag and drop). */}
            <div className="hidden md:block" aria-label="Dispatch grid">
              {view === 'day' ? (
                <DayGrid board={gridBoard ?? board} columns={columns} axis={axis} scrollRef={boardScroll} cardHandlers={cardHandlers} allowDrop={allowDrop} onDrop={handleDrop} onOptimize={openOptimize} />
              ) : (
                <WeekGrid board={gridBoard ?? board} columns={columns} cardHandlers={cardHandlers} allowDrop={allowDrop} onDrop={handleDrop} onDay={day => go(day, 'day')} />
              )}
            </div>
            {/* Phones: one list per technician, dialog only. */}
            <div className="md:hidden" aria-label="Dispatch list">
              <StackedList board={gridBoard ?? board} columns={columns} onOpen={setDrawerJob} onSchedule={openDialog} onDay={day => go(day, 'day')} onOptimize={openOptimize} />
            </div>
            </>}
          </div>

          <aside ref={backlogPanel} id="dispatch-backlog" role={phoneBacklogModal ? 'dialog' : undefined} aria-modal={phoneBacklogModal ? true : undefined} className={`${backlogOpen ? 'block' : 'hidden'} fixed inset-0 z-30 bg-black/40 p-3 md:static md:w-full md:bg-transparent md:p-0 xl:w-80 xl:shrink-0`} aria-label="Backlog" onDragOver={allowDrop} onDrop={(event) => { event.preventDefault(); handleDrop({ kind: 'backlog' }); }}>
            <section className="ml-auto max-h-full w-full max-w-md overflow-y-auto rounded-lg bg-white p-4 shadow-xl xl:max-h-[78vh] xl:max-w-none xl:shadow">
              <div className="flex items-center justify-between"><h2 className="text-lg font-semibold text-gray-900">Backlog · {backlog.length} loaded{backlogCursor ? '+' : ''}</h2><button type="button" className="text-sm font-medium text-blue-700" onClick={() => { setBacklogOpen(false); backlogToggle.current?.focus(); }}>Close backlog</button></div>
              <p className="text-xs text-gray-500">Jobs with no date or no technician. Dated, unassigned jobs also appear in the board queue. Drop a card here to unschedule it.</p>
              {bulkJobs.length > 0 && (
                <button type="button" className={`${secondaryButtonClass} mt-2 w-full`} onClick={openBulk}>
                  Suggest for the visible backlog
                </button>
              )}
              <form className="mt-3 flex gap-2" onSubmit={(event) => { event.preventDefault(); setAppliedSearch(search.trim()); }}>
                <label htmlFor="backlog-search" className="sr-only">Search backlog</label>
                <input ref={backlogSearch} id="backlog-search" type="search" className={inputClass} placeholder="Customer, address or notes" value={search} onChange={(event) => setSearch(event.target.value)} />
                <button type="submit" className={secondaryButtonClass}>Search</button>
              </form>
              {shownBacklog.length === 0 ? (
                <p className="mt-4 text-sm text-gray-600">{bundleOnly && backlog.length > 0 ? 'No bundle visits waiting to be scheduled.' : 'Nothing waiting to be scheduled.'}</p>
              ) : (
                <ul className="mt-3 space-y-2">
                  {shownBacklog.map((job) => (
                    <li key={job.id} draggable={job.schedulable} onDragStart={(event) => onDragStart(job, event)}
                      className={`rounded border-l-4 p-2 text-sm ${statusClasses(job.status)} cursor-grab`} data-backlog-job={job.id}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <Link to={`/jobs/${job.id}`} className="font-semibold text-blue-700 hover:underline">{job.customer.name}</Link>
                          {enrollmentRefOf(job) && <div className="mt-0.5"><JobEnrollmentChip job={job} /></div>}
                          <div className="flex min-w-0 items-center gap-1 text-gray-600"><span className="truncate">{job.property.address}</span>{locationUnknown(job) && <LocationUnknownMarker />}</div>
                          <div className="text-xs text-gray-500">
                            {jobStatusLabel(job.status as types.JobStatus)} · {job.scheduledDate ? formatJobDate(job.scheduledDate) : 'No date'}
                            {' · '}{job.assignedToEmail ?? 'Unassigned'} · {formatDuration(job.durationMinutes)}
                          </div>
                        </div>
                        <div className="flex shrink-0 flex-col items-end gap-1">
                          <button type="button" className="text-xs font-semibold text-blue-700 hover:underline" onClick={() => openDialog(job)}>
                            Schedule…
                          </button>
                          {job.schedulable && (
                            <button type="button" className="text-xs font-semibold text-blue-700 hover:underline" aria-label={`Suggest slots for ${job.customer.name}`} onClick={() => openSuggest(job)}>
                              Suggest
                            </button>
                          )}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              {backlogCursor && <button type="button" className={`${secondaryButtonClass} mt-3 w-full`} onClick={loadMoreBacklog}>Load more</button>}
            </section>
          </aside>
        </div>
      )}

      {drawerJob && (
        <JobDrawer job={drawerJob} technicians={allTechnicians} routes={board?.routes ?? []} onClose={() => setDrawerJob(null)}
          onSchedule={() => openDialog(drawerJob)} onSuggest={() => openSuggest(drawerJob)} />
      )}
      {dialogJob && (
        <ScheduleDialog key={chosen ? `${chosen.prefill.assignedTo}-${chosen.prefill.scheduledDate}-${chosen.prefill.scheduledStartTime}` : 'manual'}
          job={dialogJob} technicians={allTechnicians} api={api} onDone={afterOutcome} onClose={closeDialog}
          prefill={chosen?.prefill} suggestion={chosen?.suggestion}
          onSuggest={dialogSource ? () => openSuggest(dialogSource) : undefined} />
      )}
      {suggestFor && (
        <SuggestPanel
          job={{
            id: suggestFor.job.id,
            customerName: suggestFor.job.customer.name,
            address: suggestFor.job.property.address,
            scheduledDate: suggestFor.job.scheduledDate,
            assignedTo: suggestFor.job.assignedTo,
            assignedToEmail: suggestFor.job.assignedToEmail,
          }}
          api={api}
          notice={suggestFor.notice}
          onUse={(candidate) => applySuggestion(suggestFor.job, candidate)}
          onClose={() => setSuggestFor(null)}
        />
      )}
      {optimizeFor && (
        <OptimizeDayDialog
          userId={optimizeFor.userId}
          technicianLabel={optimizeFor.label}
          date={optimizeFor.date}
          api={api}
          onApplied={(result, preview) => afterOptimize(
            result.applied === 0
              ? 'Nothing needed to change.'
              : `Route applied: ${result.applied} job${result.applied === 1 ? '' : 's'} moved, about ${formatDuration(preview.minutesSaved)} less driving.`
          )}
          onChanged={() => afterOptimize(OPTIMIZE_CHANGED_MESSAGE)}
          onClose={() => setOptimizeFor(null)}
        />
      )}
      {bulkOpen && (
        <BulkSuggestDialog
          jobs={bulkJobs}
          more={backlog.filter((job) => job.schedulable).length > bulkJobs.length || Boolean(backlogCursor)}
          api={api}
          onApplied={(result) => { setBulkOpen(false); afterOptimize(bulkAppliedMessage(result)); }}
          onClose={() => setBulkOpen(false)}
        />
      )}
      {confirmation.message && <ScheduleConfirmation message={confirmation.message} onAnswer={confirmation.answer} />}
    </main>
  );
}

interface Column { id: string | null; label: string; hours: types.WorkHours | null }
type CardHandlers = Pick<CardProps, 'onOpen' | 'onSchedule' | 'onDragStart'>;

function DayGrid({ board, columns, axis, scrollRef, cardHandlers, allowDrop, onDrop, onOptimize }: {
  board: types.DispatchBoard;
  columns: Column[];
  axis: DayAxis;
  scrollRef: React.RefObject<HTMLDivElement>;
  cardHandlers: CardHandlers;
  allowDrop: (event: React.DragEvent) => void;
  onDrop: (target: DropTarget) => void;
  onOptimize: (userId: string, label: string, date: string) => void;
}) {
  const technicianColumns = columns.filter((column) => column.id !== null);
  const unassigned = jobsFor(board.jobs, null, board.date);
  const unassignedJobs = [...unassigned.untimed, ...unassigned.timed];
  const [allUnassignedOpen, setAllUnassignedOpen] = useState(false);
  const [untimedOpen, setUntimedOpen] = useState(false);
  const [preview, setPreview] = useState<{ column: string; time: string | null; top?: number } | null>(null);
  const [unassignedNoTimeTarget, setUnassignedNoTimeTarget] = useState(false);
  const noTimeHeight = untimedOpen ? Math.max(112, ...technicianColumns.map((column) => jobsFor(board.jobs, column.id, board.date).untimed.length * 90 + 52)) : 112;
  const height = axisHeight(axis);
  const date = board.date;
  const nowTop = board.today === date && board.nowMinutes >= axis.startMinutes && board.nowMinutes <= axis.endMinutes
    ? (board.nowMinutes - axis.startMinutes) * PIXELS_PER_MINUTE : null;
  return (
    <div className="space-y-3">
      <section aria-label="Unassigned queue" className="rounded-lg border border-amber-200 bg-amber-50 p-3 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div><h2 className="font-semibold text-gray-900">Unassigned queue · {unassignedJobs.length}</h2><p className="text-xs text-gray-600">Dated jobs without a technician. Drag here to clear the assignee, keeping the time.</p></div>
          <div className={`rounded border-2 border-dashed px-3 py-2 text-xs font-semibold ${unassignedNoTimeTarget ? 'border-blue-500 bg-blue-100 text-blue-900' : 'border-amber-400 bg-white text-amber-900'}`}
            onDragOver={(event) => { allowDrop(event); if (event.defaultPrevented) setUnassignedNoTimeTarget(true); }}
            onDragLeave={() => setUnassignedNoTimeTarget(false)}
            onDrop={(event) => { event.preventDefault(); setUnassignedNoTimeTarget(false); onDrop({ kind: 'noTime', assignedTo: null, date: board.date }); }}>
            Drop here for no time
          </div>
        </div>
        <div className={`mt-2 flex min-h-16 flex-wrap gap-2 rounded border-2 border-dashed p-2 ${preview?.column === UNASSIGNED ? 'border-blue-500 bg-blue-50' : 'border-amber-300 bg-white/70'}`}
          onDragOver={(event) => { allowDrop(event); if (event.defaultPrevented) setPreview({ column: UNASSIGNED, time: null }); }}
          onDragLeave={() => setPreview(null)}
          onDrop={(event) => { event.preventDefault(); setPreview(null); onDrop({ kind: 'cell', assignedTo: null, date: board.date }); }}>
          {unassignedJobs.length === 0 ? <p className="text-sm text-gray-600">No dated, unassigned jobs.</p> :
            (allUnassignedOpen ? unassignedJobs : unassignedJobs.slice(0, 3)).map((job) => <div key={job.id} className="w-full sm:w-56"><JobCard job={job} {...cardHandlers} /></div>)}
          {unassignedJobs.length > 3 && <button type="button" className="self-center text-sm font-semibold text-blue-700 underline" aria-expanded={allUnassignedOpen} onClick={() => setAllUnassignedOpen((open) => !open)}>{allUnassignedOpen ? 'Show fewer' : `Show all ${unassignedJobs.length} unassigned jobs`}</button>}
        </div>
      </section>
    <div ref={scrollRef} className="max-h-[72vh] overflow-auto rounded-lg bg-white shadow" onDragLeave={(event) => { if (!event.currentTarget.contains(event.relatedTarget as Node)) setPreview(null); }}>
      <div className="flex min-w-max">
        <div className="sticky left-0 z-20 w-16 shrink-0 border-r border-gray-200 bg-white">
          <div className="sticky top-0 z-30 h-14 border-b border-gray-200 bg-white" />
          <div className="border-b border-gray-200 bg-gray-50 px-1 py-2 text-[11px] text-gray-600" style={{ height: noTimeHeight }}><button type="button" className="font-semibold text-blue-700 underline" aria-expanded={untimedOpen} onClick={() => setUntimedOpen((open) => !open)}>No time {untimedOpen ? '−' : '+'}</button></div>
          <div className="relative" style={{ height }}>
            {axisHours(axis).map((m) => (
              <div key={m} className="absolute right-1 -translate-y-1/2 bg-white/90 text-[11px] text-gray-600" style={{ top: (m - axis.startMinutes) * PIXELS_PER_MINUTE }}>
                {m > axis.startMinutes ? formatTime(`${String(m / 60).padStart(2, '0')}:00`) : ''}
              </div>
            ))}
          </div>
        </div>
        {technicianColumns.map((column) => {
          const { timed, untimed } = jobsFor(board.jobs, column.id, date);
          const lanes = layoutLanes(timed);
          const legs = travelLegsByJob(board, column.id, date);
          const shades = column.id && column.hours ? shadeBlocks(column.hours, board.timeOff, column.id, date, axis) : [];
          return (
            <div key={column.id ?? UNASSIGNED} className={`${COLUMN_WIDTH} border-r border-gray-200`} data-column={column.id ?? UNASSIGNED}>
              <div className="sticky top-0 z-20 h-14 border-b border-gray-200 bg-white px-2 py-1 text-sm font-semibold text-gray-900" title={column.label}><div className="truncate">{column.label}</div><div className="flex items-center justify-between gap-1 text-xs font-normal text-gray-600"><span className="truncate">{timed.length + untimed.length} jobs · {[...timed, ...untimed].reduce((n, job) => n + job.conflicts.length, 0)} warnings</span>{column.id && timed.length > 1 && date >= board.today && <button type="button" className="shrink-0 font-semibold text-blue-700 hover:underline" aria-label={`Optimize route for ${column.label}`} onClick={() => onOptimize(column.id!, column.label, date)}>Optimize route</button>}</div></div>
              <div className={`space-y-1 border-b border-gray-200 p-1 ${preview?.column === (column.id ?? UNASSIGNED) && preview.time === null ? 'bg-blue-100 ring-2 ring-inset ring-blue-500' : 'bg-gray-50'}`} style={{ height: noTimeHeight }} aria-label={`No time set, ${column.label}`}
                onDragOver={(event) => { allowDrop(event); if (event.defaultPrevented) setPreview({ column: column.id ?? UNASSIGNED, time: null }); }} onDrop={(event) => { event.preventDefault(); setPreview(null); onDrop({ kind: 'noTime', assignedTo: column.id, date }); }}>
                <div className="text-xs font-medium text-gray-600">{untimed.length} dated job{untimed.length === 1 ? '' : 's'} without a time</div>
                {(untimedOpen ? untimed : untimed.slice(0, 1)).map((job) => <JobCard key={job.id} job={job} compact {...cardHandlers} />)}
                {!untimedOpen && untimed.length > 1 && <button type="button" className="text-xs font-semibold text-blue-700 underline" onClick={() => setUntimedOpen(true)}>Show {untimed.length - 1} more</button>}
              </div>
              <div className={`relative ${preview?.column === (column.id ?? UNASSIGNED) && preview.time ? 'bg-blue-50/60 ring-2 ring-inset ring-blue-500' : ''}`} style={{ height }} aria-label={`Schedule, ${column.label}`}
                onDragOver={(event) => { allowDrop(event); if (event.defaultPrevented) { const rect = event.currentTarget.getBoundingClientRect(); const offsetY = event.clientY - rect.top; setPreview({ column: column.id ?? UNASSIGNED, time: dropTime(offsetY, axis), top: offsetY }); } }}
                onDrop={(event) => {
                  event.preventDefault();
                  const rect = event.currentTarget.getBoundingClientRect();
                  setPreview(null);
                  onDrop({ kind: 'grid', assignedTo: column.id, date, offsetY: event.clientY - rect.top });
                }}>
                {preview?.column === (column.id ?? UNASSIGNED) && preview.time && <div className="pointer-events-none absolute inset-x-0 z-20 border-t-2 border-blue-600" style={{ top: preview.top }}><span className="rounded bg-blue-700 px-1.5 py-0.5 text-xs font-semibold text-white">{column.label} · {formatDayHeading(date)} · {formatTime(preview.time)}</span></div>}
                {axisHours(axis).map((m) => (
                  <div key={m} className="pointer-events-none absolute inset-x-0 border-t border-gray-100" style={{ top: (m - axis.startMinutes) * PIXELS_PER_MINUTE }} />
                ))}
                {shades.map((s, i) => (
                  <div key={i} title={s.label} className={`pointer-events-none absolute inset-x-0 ${s.label.startsWith('Time off') ? 'bg-red-100/70' : 'bg-gray-200/60'}`} style={{ top: s.top, height: s.height }} />
                ))}
                {nowTop !== null && <div className="pointer-events-none absolute inset-x-0 z-10 border-t-2 border-red-500" style={{ top: nowTop }} aria-hidden="true" />}
                {timed.map((job) => {
                  const geometry = cardGeometry(job.scheduledStartTime!, job.durationMinutes, axis);
                  const lane = lanes.get(job.id) ?? { lane: 0, lanes: 1 };
                  return (
                    <JobCard key={job.id} job={job} compact={geometry.height < 72} {...cardHandlers}
                      style={{ position: 'absolute', top: geometry.top, height: geometry.height, left: `${(lane.lane / lane.lanes) * 100}%`, width: `${100 / lane.lanes}%` }} />
                  );
                })}
                {timed.map((job) => {
                  // Drive into this card from the previous one (not the first leg from the company address).
                  const leg = legs.get(job.id);
                  const previous = leg?.fromJobId ? timed.find((j) => j.id === leg.fromJobId) : undefined;
                  if (!leg || !previous) return null;
                  const prev = cardGeometry(previous.scheduledStartTime!, previous.durationMinutes, axis);
                  const next = cardGeometry(job.scheduledStartTime!, job.durationMinutes, axis);
                  const lane = lanes.get(job.id) ?? { lane: 0, lanes: 1 };
                  return (
                    <div key={`travel-${job.id}`} className="pointer-events-none absolute z-10 flex justify-center"
                      style={{ top: travelChipTop(prev.top + prev.height, next.top), height: TRAVEL_CHIP_HEIGHT, left: `${(lane.lane / lane.lanes) * 100}%`, width: `${100 / lane.lanes}%` }}>
                      <TravelChip leg={leg} unit={board.distanceUnit} className="pointer-events-auto" />
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
    </div>
  );
}

function WeekGrid({ board, columns, cardHandlers, allowDrop, onDrop, onDay }: {
  board: types.DispatchBoard;
  columns: Column[];
  cardHandlers: CardHandlers;
  allowDrop: (event: React.DragEvent) => void;
  onDrop: (target: DropTarget) => void;
  onDay: (date: string) => void;
}) {
  const dates = weekDates(board.from);
  return (
    <div className="overflow-x-auto rounded-lg bg-white shadow">
      <table className="min-w-full table-fixed border-collapse text-sm">
        <thead>
          <tr>
            <th scope="col" className="w-40 border-b border-r border-gray-200 px-2 py-2 text-left font-semibold">Technician</th>
            {dates.map((d) => (
              <th key={d} scope="col" className={`border-b border-r border-gray-200 px-2 py-2 text-left font-semibold ${d === board.today ? 'bg-blue-50' : ''}`}><button type="button" className="text-blue-700 underline" onClick={() => onDay(d)}>{formatDayHeading(d)}</button></th>
            ))}
          </tr>
        </thead>
        <tbody>
          {columns.map((column) => (
            <tr key={column.id ?? UNASSIGNED}>
              <th scope="row" className="truncate border-b border-r border-gray-200 px-2 py-2 text-left align-top font-medium" title={column.label}>{column.label}</th>
              {dates.map((d) => {
                const { timed, untimed } = jobsFor(board.jobs, column.id, d);
                const off = column.id ? timeOffOn(board.timeOff, column.id, d) : [];
                const notWorking = column.hours ? !column.hours.workDays.includes(new Date(`${d}T00:00:00Z`).getUTCDay()) : false;
                return (
                  <td key={d} className={`h-24 min-w-[9rem] space-y-1 border-b border-r border-gray-200 p-1 align-top ${notWorking ? 'bg-gray-100' : ''}`}
                    aria-label={`${column.label}, ${formatDayHeading(d)}`}
                    onDragOver={allowDrop} onDrop={(event) => { event.preventDefault(); onDrop({ kind: 'cell', assignedTo: column.id, date: d }); }}>
                    <div className="text-xs font-semibold text-gray-700">{timed.length + untimed.length} jobs · {formatDuration([...timed, ...untimed].reduce((n, job) => n + job.durationMinutes, 0))}</div>
                    {dayTravelSummary(board, column.id, d) && <div className="text-[11px] text-gray-600" data-travel-total>{dayTravelSummary(board, column.id, d)}</div>}
                    {off.map((t) => (
                      <div key={t.id} className="rounded bg-red-100 px-1 text-[11px] text-red-800">
                        Off{t.startTime ? ` ${formatTime(t.startTime)}-${formatTime(t.endTime)}` : ''}{t.reason ? `: ${t.reason}` : ''}
                      </div>
                    ))}
                    {[...untimed, ...timed].map((job) => <JobCard key={job.id} job={job} compact {...cardHandlers} />)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Route view (tracker item 57): one row per route (Unrouted last), one column
 * per day, jobs by their stored route. Coverage is informational; scheduling
 * still happens through each card's Schedule dialog.
 */
function RouteBoard({ board, cardHandlers, onDay }: {
  board: types.DispatchBoard;
  cardHandlers: CardHandlers;
  onDay: (date: string) => void;
}) {
  const week = board.view === 'week';
  const dates = week ? weekDates(board.from) : [board.date];
  return (
    <section aria-label="Route board" className="rounded-lg bg-white shadow">
      <p className="px-3 pt-3 text-xs text-gray-600">
        Jobs are grouped by their saved route. Technician coverage is for reference only and never limits scheduling.
      </p>
      <div className="overflow-x-auto p-1">
        <table className="min-w-full border-collapse text-sm">
          <thead>
            <tr>
              <th scope="col" className="w-40 border-b border-r border-gray-200 px-2 py-2 text-left font-semibold">Route</th>
              {dates.map((d) => (
                <th key={d} scope="col" className={`border-b border-r border-gray-200 px-2 py-2 text-left font-semibold ${d === board.today ? 'bg-blue-50' : ''}`}>
                  {week ? <button type="button" className="text-blue-700 underline" onClick={() => onDay(d)}>{formatDayHeading(d)}</button> : formatDayHeading(d)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {routeGroups(board).map((routeGroup) => (
              <tr key={routeGroup.id ?? UNROUTED_GROUP} data-route={routeGroup.id ?? UNROUTED_GROUP}>
                <th scope="row" className="break-words border-b border-r border-gray-200 px-2 py-2 text-left align-top font-medium">
                  {routeGroup.name}{!routeGroup.isActive && routeGroup.id && <span className="block text-xs font-normal text-gray-500">Inactive</span>}
                </th>
                {dates.map((d) => {
                  const jobs = routeJobs(board.jobs, routeGroup.id, d);
                  const coverage = routeGroup.id ? coverageLabel(routeCoverage(board, routeGroup.id, d)) : null;
                  return (
                    <td key={d} className={`space-y-1 border-b border-r border-gray-200 p-1 align-top ${week ? 'min-w-[10rem]' : 'min-w-[18rem]'}`}
                      aria-label={`${routeGroup.name}, ${formatDayHeading(d)}`}>
                      <div className="text-xs font-semibold text-gray-700">{jobs.length} job{jobs.length === 1 ? '' : 's'} · {formatDuration(jobs.reduce((n, job) => n + job.durationMinutes, 0))}</div>
                      {routeGroup.id && (
                        <div className="text-[11px] text-gray-600" data-coverage>
                          {coverage ? `Covered by ${coverage}` : 'No technician covering'}
                        </div>
                      )}
                      {jobs.map((job) => <JobCard key={job.id} job={job} compact={week} routeView {...cardHandlers} />)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function StackedList({ board, columns, onOpen, onSchedule, onDay, onOptimize }: {
  board: types.DispatchBoard;
  columns: Column[];
  onOpen: (job: types.BoardJob) => void;
  onSchedule: (job: types.BoardJob) => void;
  onDay: (date: string) => void;
  onOptimize: (userId: string, label: string, date: string) => void;
}) {
  const dates = board.view === 'week' ? weekDates(board.from) : [board.date];
  return (
    <div className="space-y-4">
      {dates.map((date) => {
        const activeColumns = columns.filter((column) => board.jobs.some((job) => job.scheduledDate === date && job.assignedTo === column.id)
          || Boolean(column.id && timeOffOn(board.timeOff, column.id, date).length));
        return <section key={date} aria-label={formatDayHeading(date)} className="space-y-3">
        <div className="flex items-center justify-between"><h2 className="text-lg font-semibold text-gray-900">{formatDayHeading(date)}</h2>{board.view === 'week' && <button type="button" className="text-sm font-semibold text-blue-700 underline" onClick={() => onDay(date)}>Open day</button>}</div>
      {activeColumns.length === 0 && <p className="rounded-lg bg-white p-4 text-sm text-gray-600 shadow">No jobs or time off this day.</p>}
      {activeColumns.map((column) => {
        const jobs = timeOrdered(board.jobs.filter((j) => j.assignedTo === column.id && j.scheduledDate === date));
        const off = column.id ? timeOffOn(board.timeOff, column.id, date) : [];
        const legs = travelLegsByJob(board, column.id, date);
        return (
          <section key={column.id ?? UNASSIGNED} className="rounded-lg bg-white p-4 shadow" aria-label={column.label}>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <h2 className="min-w-0 break-words font-semibold text-gray-900">{column.label}</h2>
              {column.id && jobs.filter((j) => j.scheduledStartTime).length > 1 && date >= board.today && (
                <button type="button" className="shrink-0 text-sm font-semibold text-blue-700 underline" aria-label={`Optimize route for ${column.label}`} onClick={() => onOptimize(column.id!, column.label, date)}>Optimize route</button>
              )}
            </div>
            {column.hours && <p className="text-xs text-gray-500">Hours {formatTime(column.hours.startTime)} - {formatTime(column.hours.endTime)}{dayTravelSummary(board, column.id, date) ? ` · ${dayTravelSummary(board, column.id, date)}` : ''}</p>}
            {off.map((t) => <p key={t.id} className="mt-1 text-xs text-red-700">Off{t.startTime ? ` ${formatTime(t.startTime)}-${formatTime(t.endTime)}` : ''}{t.reason ? `: ${t.reason}` : ''}</p>)}
            {jobs.length === 0 ? <p className="mt-2 text-sm text-gray-600">No jobs.</p> : (
              <ul className="mt-2 divide-y divide-gray-100">
                {jobs.map((job) => (
                  <li key={job.id} className="flex items-start justify-between gap-3 py-2">
                    <button type="button" className="min-w-0 text-left" onClick={() => onOpen(job)}>
                      {legs.get(job.id)?.fromJobId && <TravelChip leg={legs.get(job.id)!} unit={board.distanceUnit} className="mb-1" />}
                      <span className="block text-sm font-semibold text-gray-900">
                        {board.view === 'week' && job.scheduledDate ? `${formatDayHeading(job.scheduledDate)} · ` : ''}
                        {formatTimeRange(job.scheduledStartTime, job.durationMinutes)}
                        <ConflictBadge conflicts={job.conflicts} />
                      </span>
                      <span className="block truncate text-sm">{job.customer.name}</span>
                      <span className="flex min-w-0 items-center gap-1 text-xs text-gray-600"><span className="truncate">{job.property.address} · {jobStatusLabel(job.status as types.JobStatus)}</span>{locationUnknown(job) && <LocationUnknownMarker compact />}</span>
                    </button>
                    {job.schedulable && (
                      <button type="button" className={`${secondaryButtonClass} shrink-0`} onClick={() => onSchedule(job)}>Schedule…</button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>
        );
      })}
      </section>;
      })}
    </div>
  );
}

function JobDrawer({ job, technicians, routes, onClose, onSchedule, onSuggest }: {
  job: types.BoardJob;
  technicians: types.BoardTechnician[];
  routes: types.DispatchRoute[];
  onClose: () => void;
  onSchedule: () => void;
  onSuggest: () => void;
}) {
  const closeButton = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLElement>(null);
  useEffect(() => {
    const origin = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    closeButton.current?.focus();
    return () => origin?.focus();
  }, []);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      if (event.key !== 'Tab') return;
      const focusable = panel.current?.querySelectorAll<HTMLElement>('button, a[href]');
      if (!focusable?.length) return;
      const first = focusable[0], last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const tech = technicians.find((t) => t.id === job.assignedTo);
  return (
    <aside ref={panel} role="dialog" aria-modal="true" aria-label="Job summary" className="fixed inset-y-0 right-0 z-40 w-full max-w-sm overflow-y-auto bg-white p-5 shadow-xl">
      <div className="flex items-start justify-between gap-2">
        <h2 className="break-words text-lg font-semibold text-gray-900">{job.customer.name}</h2>
        <button ref={closeButton} type="button" className={secondaryButtonClass} onClick={onClose}>Close</button>
      </div>
      <dl className="mt-4 space-y-2 text-sm">
        <div><dt className="text-gray-500">Address</dt><dd className="break-words">{job.property.address}, {job.property.city}, {job.property.state}</dd></div>
        <div><dt className="text-gray-500">Status</dt><dd>{jobStatusLabel(job.status as types.JobStatus)}</dd></div>
        <div><dt className="text-gray-500">When</dt><dd>{job.scheduledDate ? formatJobDate(job.scheduledDate) : 'No date'} · {formatTimeRange(job.scheduledStartTime, job.durationMinutes)}</dd></div>
        <div><dt className="text-gray-500">Duration</dt><dd>{formatDuration(job.durationMinutes)} {job.durationSource === 'MANUAL' ? '(manual)' : '(from lines)'}</dd></div>
        {job.arrivalWindowMinutes && job.scheduledStartTime && <div><dt className="text-gray-500">Arrival window</dt><dd>{formatArrivalWindow(job.scheduledStartTime, job.arrivalWindowMinutes)}</dd></div>}
        <div><dt className="text-gray-500">Route</dt><dd className="break-words">{routeName(routes, job.routeId)}</dd></div>
        <div><dt className="text-gray-500">Technician</dt><dd className="break-words">{tech?.email ?? job.assignedToEmail ?? 'Unassigned'}</dd></div>
        {job.lineSummary && <div><dt className="text-gray-500">Work</dt><dd className="break-words">{job.lineSummary}</dd></div>}
        {enrollmentRefOf(job) && <div><dt className="text-gray-500">Service bundle</dt><dd><JobEnrollmentChip job={job} /></dd></div>}
        {job.notes && <div><dt className="text-gray-500">Notes</dt><dd className="whitespace-pre-wrap break-words">{job.notes}</dd></div>}
        {job.conflicts.length > 0 && (
          <div><dt className="text-gray-500">Warnings</dt><dd><ul className="list-disc pl-5 text-red-700">{job.conflicts.map((c, i) => <li key={i}>{conflictLabel(c.type)}: {c.message}</li>)}</ul></dd></div>
        )}
      </dl>
      <div className="mt-5 flex flex-col gap-2">
        {job.schedulable ? (
          <>
            <button type="button" className={primaryButtonClass} onClick={onSchedule}>Schedule…</button>
            <button type="button" className={secondaryButtonClass} onClick={onSuggest}>Suggest…</button>
          </>
        ) : (
          <p className="text-sm text-gray-600">This job is {jobStatusLabel(job.status as types.JobStatus).toLowerCase()} and can no longer be moved.</p>
        )}
        <Link to={`/jobs/${job.id}`} className={`${secondaryButtonClass} text-center`}>Open job</Link>
        {job.scheduledDate && job.assignedTo && (
          <Link to={dispatchPrintPath(job.scheduledDate, job.assignedTo)} className={`${secondaryButtonClass} text-center`}>Print day sheet</Link>
        )}
      </div>
    </aside>
  );
}
